import * as React from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { parse } from '@pec/aion-ui-core/helpers/querystring';
import { useTypedSelector } from '../../../combineReducers';
import { SELECTED_PRODUCT } from './questionTypes/MuiSelectableSubscriptions';
import { TRANSACTION_ID_KEY } from '../helpers/registerSurvayCallbacks';
import { OperationType } from '../types/enums';
import { checkCompletionOnServer } from '../helpers/complete';

type RouteParams = {
  organizationId: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(3),
      gap: `${theme.spacing(4)}px`,
      color: 'var(--text-black)',
      lineHeight: '1.5rem'
    },
    headLine: {
      fontSize: '1.5rem',
      letterSpacing: '0.01125em'
    },
    text: {
      fontWeight: 500
    },
    emailConfirmation: {
      color: 'var(--black)',
      fontSize: 'var(--font-size-14)',
      lineHeight: '1.25rem'
    },
    button: {
      marginLeft: '6px',
      border: '1px solid var(--on-surface-outline-000000-12-)'
    }
  })
);

export const SuccessScreen: React.FC = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const { organization } = useTypedSelector(state => state.organization);
  const history = useHistory();
  const { search } = useLocation();
  const classes = useStyles();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation();
  const operationType = parse(search).operationType as OperationType;
  const selectedProductRef = React.useRef<string>(
    sessionStorage.getItem(`${SELECTED_PRODUCT}${organizationId}_${operationType}`)
  );
  const product = selectedProductRef.current && JSON.parse(selectedProductRef.current);
  const purchaseExpected = operationType !== OperationType.ScopeChange;

  React.useEffect(() => {
    (async () => {
      if (purchaseExpected) {
        let purchaseResolve: (value?: unknown) => void = () => {};
        const purchasePromise = new Promise(resolve => {
          purchaseResolve = resolve;
        });

        let gtmResolve: (value?: unknown) => void = () => {};
        const gtmPromise = new Promise(resolve => {
          gtmResolve = resolve;
        });

        if (window['dataLayer']) {
          gtmResolve();
        } else {
          window.addEventListener('gtmLoaded', () => gtmResolve(), { once: true });
        }

        const purchaseInfoJSON = sessionStorage.getItem('purchaseInfo');
        const purchaseInfo = purchaseInfoJSON && JSON.parse(purchaseInfoJSON);

        sessionStorage.clear();

        const key = `${TRANSACTION_ID_KEY}${organizationId}_${operationType}`;
        const transactionId = JSON.parse(localStorage.getItem(key) as string);
        const res = await checkCompletionOnServer(transactionId);

        if (res === false) {
          history.push('/');
        }

        localStorage.removeItem(key);
        setIsLoading(false);
        purchaseResolve();

        try {
        } catch (err) {
          history.push('/');
        }

        Promise.all([purchasePromise, gtmPromise]).then(() => {
          const dataLayer = window['dataLayer'];
          try {
            if (dataLayer) {
              dataLayer.push({ ecommerce: null });
              dataLayer.push(purchaseInfo);
            }
          } catch {}
        });
      } else {
        setIsLoading(false);
      }
    })();
  }, [history, organizationId, operationType, purchaseExpected]);

  const getStrings = (operationType: OperationType): { title: string; description: string } => {
    switch (operationType) {
      case OperationType.ScopeChange:
        return {
          title: t('subscriber:survey.successPage.textScopeChange', 'Thank you!'),
          description: t(
            'subscriber:survey.successPage.descriptionScopeChange',
            'Your change request has been sent. Our team will reach out soon to guide you through the membership update process.'
          )
        };
      case OperationType.Renew:
        return {
          title: t('subscriber:survey.successPage.textRenew', 'Thank you for renewing your CHAS membership!'),
          description: t(
            'subscriber:survey.successPage.emailConfirmation',
            'You will receive a confirmation email shortly.'
          )
        };
      case OperationType.Upgrade:
        return {
          title: t('subscriber:survey.successPage.textUpgrade', {
            defaultValue: 'Thank you for upgrading to {{value}}!',
            value: product?.itemName
          }),
          description: t(
            'subscriber:survey.successPage.emailConfirmation',
            'You will receive a confirmation email shortly.'
          )
        };
      default:
        return {
          title: t('subscriber:survey.successPage.textUpgrade', {
            defaultValue: 'Thank you for upgrading to {{value}}!',
            value: product?.itemName
          }),
          description: t(
            'subscriber:survey.successPage.emailConfirmation',
            'You will receive a confirmation email shortly.'
          )
        };
    }
  };

  return (
    <Box display="flex" width="100%" height="100%">
      {isLoading && <Loading />}
      {!isLoading && (
        <Box>
          <div className={classes.contentContainer}>
            <Typography className={classes.headLine} display="block" variant="h6">
              {organization?.name}
            </Typography>
            <Box display="flex" alignItems="center" marginBottom={2}>
              {purchaseExpected && (
                <img
                  width={72}
                  height={72}
                  src={`${process.env.PUBLIC_URL}${product?.itemSrc}`}
                  alt={t('subscriber:survey.successPage.productIcon', 'Selected product icon')}
                />
              )}
              <Box marginLeft={1}>
                <Typography className={classes.text} variant="h6">
                  {getStrings(operationType).title}
                </Typography>
                <Typography className={classes.emailConfirmation}>{getStrings(operationType).description}</Typography>
              </Box>
            </Box>
            <Box>
              <Button href={`/organizations/${organizationId}/questionnaire-v2`} variant="contained" color="primary">
                {t('subscriber:survey.successPage.toQuestionnaire', 'Go to Questionnaire')}
              </Button>
              <Button
                href={`/organizations/${organizationId}`}
                color="primary"
                className={classes.button}
                variant="outlined"
              >
                {t('subscriber:survey.successPage.toDashboard', 'Go to my dashboard')}
              </Button>
            </Box>
          </div>
        </Box>
      )}
    </Box>
  );
};
